import type { SxProps, Theme } from "@mui/material";
import { Autocomplete, TextField } from "@mui/material";
import { usePassShapesLookup } from "../hooks/usePassIds";

interface PassShapeSelectorProps {
	value: number | null;
	onChange: (value: number | null) => void;
	label?: string;
	fullWidth?: boolean;
	size?: "small" | "medium";
	required?: boolean;
	error?: boolean;
	helperText?: string;
	/** Adds an "All" option. There is no clear button: this value never accepted empty. */
	includeAll?: boolean;
	disabled?: boolean;
	sx?: SxProps<Theme>;
}

type Option = { Id: number; Name: string };

const ALL_OPTION: Option = { Id: 0, Name: "All" };

// Typed as boolean, not `true`: the value is still null on a new record until something is picked.
const NO_CLEAR_BUTTON = true as boolean;

export const PassShapeSelector = ({
	value,
	onChange,
	label = "Pass Shape",
	fullWidth = false,
	size = "small",
	required = false,
	error = false,
	helperText,
	includeAll = false,
	disabled = false,
	sx,
}: PassShapeSelectorProps) => {
	const { data: items = [], isLoading } = usePassShapesLookup();
	const options: Option[] = includeAll ? [ALL_OPTION, ...items] : items;
	const selected =
		options.find((option) => option.Id === value) ??
		(includeAll ? ALL_OPTION : null);

	return (
		<Autocomplete
			options={options}
			value={selected}
			size={size}
			fullWidth={fullWidth}
			disabled={disabled || isLoading}
			disableClearable={NO_CLEAR_BUTTON}
			autoHighlight
			getOptionLabel={(option) => option.Name}
			isOptionEqualToValue={(option, current) => option.Id === current.Id}
			onChange={(_event, option) =>
				onChange(!option || option.Id === ALL_OPTION.Id ? null : option.Id)
			}
			renderOption={(props, option) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={option.Id} {...rest}>
						{option.Name}
					</li>
				);
			}}
			// The list opens as wide as its longest option instead of wrapping it.
			slotProps={{ popper: { sx: { minWidth: "fit-content" } } }}
			sx={{ minWidth: 150, ...sx } as SxProps<Theme>}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					error={error}
					helperText={helperText}
					slotProps={{ inputLabel: { shrink: true } }}
				/>
			)}
		/>
	);
};
